import app from 'flarum/forum/app';
import extractText from 'flarum/common/utils/extractText';
const RTL = ['ar', 'fa', 'he', 'ku', 'ps', 'ur', 'yi'];
const escape = s => String(s).replace(/[&<>"']/g, c => `&#${c.charCodeAt(0)};`);

/** A language tag as one of Rosetta's codes. Mirrors Language::normalize on the server, which has the final say. */
export function normalizeTag(tag) {
  const [base, ...rest] = tag.toLowerCase().split(/[-_]/);
  const alias = {
    iw: 'he',
    ji: 'yi',
    in: 'id',
    no: 'nb',
    nn: 'nb',
    tl: 'fil',
    ckb: 'ku'
  };
  const code = alias[base] || base;
  if (code === 'zh') return rest.some(r => ['hant', 'tw', 'hk', 'mo'].includes(r)) ? 'zh-Hant' : 'zh-Hans';
  if (code === 'pt') return rest.some(r => ['pt', 'ao', 'mz', 'cv', 'gw', 'st', 'tl'].includes(r)) ? 'pt' : 'pt-BR';
  return /^[a-z]{2,3}$/.test(code) ? code : null;
}

/** The reader's language, taken from the browser: the first one it lists. */
export function browserLanguage() {
  const tags = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const tag of tags) {
    const code = tag ? normalizeTag(tag) : null;
    if (code) return code;
  }
  return null;
}
export const isRtl = code => !!code && RTL.includes(code.split('-')[0]);

/**
 * What the reader sees in place of each item: asked for in batches as items
 * are drawn (so only what is on screen, and anything that arrives live), and
 * kept for the visit.
 */
export default class Rosetta {
  target = 'en';
  dir = 'ltr';
  ui = {
    show_original: extractText(app.translator.trans('ernestdefoe-rosetta.forum.show_original')),
    show_translation: extractText(app.translator.trans('ernestdefoe-rosetta.forum.show_translation'))
  };

  /** Off until start(): the forum has a key, and the reader's language is known. */
  enabled = false;

  /** While true, Discussion#title() answers as written (the rename box). */
  raw = false;
  entries = new Map();
  originals = new Set();
  queue = new Set();
  timer = 0;
  names = null;

  /** Switches on, for the reader's language. */
  start(target) {
    this.target = target;
    this.dir = isRtl(target) ? 'rtl' : 'ltr';
    this.enabled = true;
    try {
      this.names = new Intl.DisplayNames([target], {
        type: 'language'
      });
    } catch {
      this.names = null;
    }
    document.addEventListener('click', e => {
      const button = e.target?.closest?.('.Rosetta-toggle');
      if (!button) return;
      e.preventDefault();
      e.stopPropagation();
      this.toggle(button.dataset.rosetta || '');
    });
  }

  /** The answer for an item, once there is one; asks for it otherwise. */
  answer(key, src) {
    if (!this.enabled) return undefined;
    let entry = this.entries.get(key);
    if (!entry || entry.src !== src) {
      entry = {
        src,
        done: false
      };
      this.entries.set(key, entry);
      this.queue.add(key);
      if (!this.timer) this.timer = window.setTimeout(() => this.flush(), 30);
    }
    return entry.done ? entry.answer : undefined;
  }
  showingOriginal(key) {
    return this.originals.has(key);
  }
  toggle(key) {
    if (this.originals.has(key)) this.originals.delete(key);else this.originals.add(key);
    this.touch(key);
    m.redraw();
  }

  /** Rendered HTML (a post, a private message) as the reader should see it. */
  html(key, src) {
    const answer = this.answer(key, src);
    if (!answer) return src;
    const page = document.documentElement.dir === 'rtl' ? 'rtl' : 'ltr';
    if (answer.text) {
      const original = this.originals.has(key);
      const body = original ? this.wrap(src, answer.from, answer.dir) : this.wrap(answer.text, this.target, this.dir);
      return body + this.note(key, answer.from, original);
    }

    // Already in the reader's language: as written, given its own direction
    // when it runs the other way to the page (Hebrew on an English forum).
    return answer.dir !== page ? this.wrap(src, answer.from, answer.dir) : src;
  }

  /** Plain text (a title, a chat message) in the reader's language. */
  text(key, src) {
    const answer = this.answer(key, src);
    return answer?.text && !this.originals.has(key) ? answer.text : src;
  }

  /** "Português · Show original": who wrote it in what, and the way back. */
  note(key, from, original) {
    const label = escape(original ? this.ui.show_translation : this.ui.show_original);
    const name = this.languageName(from);
    return `<div class="Rosetta-note" dir="${this.dir}" lang="${escape(this.target)}">` + `<button type="button" class="Rosetta-toggle" data-rosetta="${escape(key)}">` + `<i class="fas fa-language" aria-hidden="true"></i> ${name ? escape(name) + ' · ' : ''}${label}</button></div>`;
  }

  /** A language's name in the reader's own language: "inglês", "الإنجليزية". */
  languageName(code) {
    if (!code || code === 'und' || code === 'zxx' || !this.names) return '';
    try {
      const name = this.names.of(code.split('-')[0]) || '';
      return name.charAt(0).toLocaleUpperCase(this.target) + name.slice(1);
    } catch {
      return '';
    }
  }
  wrap(html, lang, dir) {
    return `<div class="Rosetta" lang="${escape(lang)}" dir="${dir}">${html}</div>`;
  }
  async flush() {
    this.timer = 0;
    const keys = [...this.queue].slice(0, 60);
    keys.forEach(key => this.queue.delete(key));
    // The entries as asked: one replaced meanwhile (an edit) is asked again.
    const asked = keys.map(key => this.entries.get(key));
    if (this.queue.size) this.timer = window.setTimeout(() => this.flush(), 30);
    let items = {};
    try {
      const response = await app.request({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/rosetta',
        body: {
          locale: this.target,
          items: keys
        },
        // A failed translation is not the reader's problem: they keep the
        // original, without an error popping up.
        errorHandler: () => {}
      });
      items = response.items || {};
      if (response.ui?.show_original) this.ui = response.ui;
    } catch {
      items = {};
    }
    keys.forEach((key, i) => {
      const entry = asked[i];
      if (!entry) return;
      entry.done = true;
      entry.answer = items[key];
      if (entry.answer?.text) this.touch(key);
    });
    m.redraw();
  }

  /**
   * Makes a redraw show an item's new text where core keeps it from being
   * redrawn: a post is only redrawn when it changes (its freshness), and the
   * browser tab's title is set once, as the discussion opens.
   */
  touch(key) {
    const [type, id] = key.split(':');
    if (type === 'post') {
      const post = app.store.getById('posts', id);
      if (post) post.freshness = new Date();
    } else if (type === 'discussion') {
      const discussion = app.current.get('discussion');
      if (discussion?.id() === id) app.setTitle(discussion.title());
    }
  }
}